import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircleIcon from "@mui/icons-material/Circle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RouteIcon from "@mui/icons-material/Route";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import PlaceIcon from "@mui/icons-material/Place";
import { useTranslation } from "react-i18next";
import { WorkShiftTimeSlotItem } from "../../../models/workShitTimeSlot/workShiftTimeSlotItem";
import {
    getWorkTimeTypeColorCode,
    getWorkTimeTypeColorClass,
} from "../../../models/workShitTimeSlot/workTimeType";
import { formatTime } from "../../workTimeBeta/workTimeDetailsList";
import { Base } from "../../../framework/base";

interface WorkTimeSlotCardProps {
    slot: WorkShiftTimeSlotItem;
    isSelected: boolean;
    onSelect: (id: string) => void;
    onHover: (id: string | null) => void;
    address?: string;
}

const DetailRow = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
    <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" noWrap>
            {text}
        </Typography>
    </Box>
);

const WorkTimeSlotCard = ({
    slot,
    isSelected,
    onSelect,
    onHover,
    address,
}: WorkTimeSlotCardProps) => {
    const { t } = useTranslation();

    const borderColor = getWorkTimeTypeColorCode(slot.workTimeTypeType);
    const colorClass = getWorkTimeTypeColorClass(slot.workTimeTypeType);
    const isDriving = slot.workTimeTypeType === 0;
    const slotItem = useMemo(() => new WorkShiftTimeSlotItem(slot), [slot]);

    const timeLabel = useMemo(() => {
        const today = Base.dayjsToJsonDate();
        const start = slot.startDate ? formatTime(slot.startDate, today) : "";
        const end = slot.endDate
            ? formatTime(slot.endDate, today)
            : t("monitorView.workTimeSlotCard.ongoing");
        return `${start} - ${end}`;
    }, [slot.startDate, slot.endDate, t]);

    const iconSx = { fontSize: 14, color: "text.secondary" };

    return (
        <Card
            sx={{
                mb: 1,
                borderLeft: `4px solid ${borderColor}`,
                backgroundColor: isSelected
                    ? "rgba(99, 164, 255, 0.15)"
                    : isDriving
                        ? "rgba(244, 67, 54, 0.05)"
                        : "inherit",
            }}
            onMouseEnter={() => onHover(slot.id)}
            onMouseLeave={() => onHover(null)}
        >
            <CardActionArea onClick={() => onSelect(slot.id)}>
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                    {/* Header: color dot + name + duration */}
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Grid item display="flex" alignItems="center" gap={0.5}>
                            <CircleIcon
                                className={`text-${colorClass}`}
                                sx={{ fontSize: 14 }}
                            />
                            <Typography variant="body2" fontWeight="bold">
                                {slot.workTimeTypeName ??
                                    t("workTimeType.noWorkTimeType")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" fontWeight="bold">
                                {slotItem.getDurationStr()}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Time range */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.25 }}
                    >
                        {timeLabel}
                    </Typography>

                    {/* Detail rows - only shown when data exists */}
                    {slot.vehicleRegisterNumber && (
                        <DetailRow
                            icon={<DirectionsCarIcon sx={iconSx} />}
                            text={slot.vehicleRegisterNumber}
                        />
                    )}
                    {slot.employeeName && (
                        <DetailRow
                            icon={<PersonIcon sx={iconSx} />}
                            text={slot.employeeName}
                        />
                    )}
                    {slot.customerName && (
                        <DetailRow
                            icon={<BusinessIcon sx={iconSx} />}
                            text={slot.customerName}
                        />
                    )}
                    {slot.costCenterName && (
                        <DetailRow
                            icon={<AccountBalanceIcon sx={iconSx} />}
                            text={slot.costCenterName}
                        />
                    )}
                    {slot.distance > 0 && (
                        <DetailRow
                            icon={<RouteIcon sx={iconSx} />}
                            text={`${Math.round(slot.distance * 10) / 10} km`}
                        />
                    )}
                    {slot.fuelConsumed > 0 && (
                        <DetailRow
                            icon={<LocalGasStationIcon sx={iconSx} />}
                            text={`${Math.round(slot.fuelConsumed * 10) / 10} L`}
                        />
                    )}
                    {address && (
                        <DetailRow
                            icon={<PlaceIcon sx={iconSx} />}
                            text={address}
                        />
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default WorkTimeSlotCard;
